import { EASINGS, Editor, TLShape, TLShapeId, TLUiActionsContextType, TLUiToolsContextType } from 'tldraw';

import {
  DISTANCE_BETWEEN_NODES,
  DISTANCE_FROM_TIMELINE,
  FIRST_NODE_X,
  NODE_HEIGHT,
  NODE_WIDTH,
  TIMELINE_Y,
} from './constants';
import {
  NodeData,
  getDocumentNames,
  calculateNodePosition,
  calculateNodeYPosition,
  createConnectingLineProps,
  calculateTimelineLength,
  createNodeShapeProps,
} from './timelineSharedUtils';
import { ChronologyFact } from '@/types';

// Function to calculate height based on content
export const calculateNodeHeight = (description: string, documentNames: string): number => {
  const baseHeight = 100; // Base padding and margins
  const dateHeight = 24; // Date line height
  const docHeight = documentNames ? 40 : 0; // Document names section

  // Calculate description height based on character count and line breaks
  const charCount = description.length;
  const lineBreaks = (description.match(/\n/g) || []).length;
  const estimatedLines = Math.max(1, Math.ceil(charCount / 60) + lineBreaks); // ~80 chars per line
  const descriptionHeight = estimatedLines * 20; // ~20px per line

  return Math.max(NODE_HEIGHT, baseHeight + dateHeight + descriptionHeight + docHeight);
};

// UI Toolbar overrides
export const overrides = {
  tools: (_editor: Editor, tools: TLUiToolsContextType) => {
    delete tools.eraser;
    delete tools.draw;
    delete tools.asset;
    delete tools.triangle;
    delete tools.cloud;
    delete tools.rectangle;
    delete tools.ellipse;
    delete tools.diamond;
    delete tools.pentagon;
    delete tools.hexagon;
    delete tools.octagon;
    delete tools.star;
    delete tools.rhombus;
    delete tools['rhombus-2'];
    delete tools.oval;
    delete tools.trapezoid;
    delete tools['arrow-right'];
    delete tools['arrow-left'];
    delete tools['arrow-up'];
    delete tools['arrow-down'];
    delete tools['x-box'];
    delete tools['check-box'];
    delete tools.heart;
    delete tools.line;
    delete tools.frame;
    delete tools.embed;
    delete tools.laser;
    return tools;
  },
  actions: (_editor: Editor, actions: TLUiActionsContextType): TLUiActionsContextType => {
    const newActions = {
      ...actions,
      // Temporarily removing undo/redo as this complicates the saved/unsaved changes work
      undo: { ...actions['undo'], kbd: '' },
      redo: { ...actions['redo'], kbd: '' },
    };

    return newActions;
  },
};

export const createTimeline = (editor: Editor, chronology: ChronologyFact[]) => {
  // Clear all existing shapes from the canvas
  clearCanvasShapes(editor);

  // Create a horizontal line for the timeline
  drawTimeline(editor, chronology.length);

  // Create nodes for each chronology entry and add year markers
  let currentYear: string | null = null;
  chronology.forEach((fact, index) => {
    const nodeX = FIRST_NODE_X + index * DISTANCE_BETWEEN_NODES;
    const isAbove = index % 2 === 0;

    // Check if we need to add a year marker
    if (fact.factDate) {
      const factYear = new Date(fact.factDate).getFullYear().toString();
      if (factYear !== currentYear) {
        currentYear = factYear;
        drawYearMarker(editor, nodeX, factYear);
      }
    }

    createChronologyNode(
      editor,
      {
        nodeId: fact.id,
        factId: fact.fact.id,
        date: fact?.factDate ?? '',
        dateText: fact.factDateText ?? '',
        description: fact.description ?? '',
        documentNames: getDocumentNames(fact),
        docId: fact.fact.locations[0]?.doc?.id ?? '',
      },
      nodeX,
      isAbove,
    );

    // If it is not the last node, create an in-between node to the right
    if (index < chronology.length - 1) {
      createInBetweenButton(editor, index);
    }
  });

  // Create the add new node button at the end
  createAddNewNodeButton(editor, chronology.length);
};

export const drawTimeline = (editor: Editor, chronologyLength: number) => {
  editor.createShape({
    id: 'shape:timeline-line' as TLShapeId,
    type: 'line',
    x: 50,
    y: TIMELINE_Y,
    isLocked: true,
    props: {
      color: 'black',
      dash: 'solid',
      size: 'm',
      spline: 'line',
      points: {
        '0': { id: '0', index: 'a0', x: 0, y: 0 },
        '1': { id: '1', index: 'a1', x: FIRST_NODE_X + chronologyLength * DISTANCE_BETWEEN_NODES, y: 0 },
      },
      scale: 1,
    },
  });
};

export const unlockShape = (editor: Editor, shape: TLShape) => {
  editor.updateShape({
    id: shape.id,
    type: shape.type,
    isLocked: false,
  });
};

export const clearCanvasShapes = (editor: Editor) => {
  const existingShapes = editor.getCurrentPageShapes();
  existingShapes.forEach((shape) => {
    unlockShape(editor, shape);
    editor.deleteShape(shape.id);
  });
};

export const drawYearMarker = (editor: Editor, nodeX: number, year: string) => {
  editor.createShape({
    type: 'year-marker',
    x: nodeX + 50,
    y: TIMELINE_Y - 20,
    props: {
      w: 50,
      h: 40,
      year: year,
    },
    isLocked: true,
  });
};

export const createChronologyNode = (editor: Editor, nodeData: NodeData, nodeX: number, isAbove: boolean) => {
  const calculatedHeight = calculateNodeHeight(nodeData.description, nodeData.documentNames);

  // Node position is top right of box, and nodes have variable height so we need to calculate top right by
  // TIMELINE_Y- DISTANCE_FROM_TIMELINE - height if it is above the line, or just TIMELINE_Y + DISTANCE_FROM_TIMELINE if below
  const nodeY = isAbove ? TIMELINE_Y - DISTANCE_FROM_TIMELINE - calculatedHeight : TIMELINE_Y + DISTANCE_FROM_TIMELINE;

  // Create the chronology node first
  editor.createShape({
    id: `shape:visual-chronology-node-${nodeData.nodeId}` as TLShapeId,
    type: 'visual-chronology-node',
    x: nodeX,
    y: nodeY,
    props: createNodeShapeProps(nodeData, calculatedHeight, isAbove),
  });

  // Create the connecting line at the x mid point of the node
  editor.createShape({
    id: `shape:connecting-line-${nodeData.nodeId}` as TLShapeId,
    type: 'line',
    x: nodeX + NODE_WIDTH / 2,
    y: TIMELINE_Y,
    isLocked: true,
    props: createConnectingLineProps(isAbove),
  });
};

export const createInBetweenButton = (editor: Editor, i: number) => {
  const nodeX1 = FIRST_NODE_X + i * DISTANCE_BETWEEN_NODES;
  const nodeX2 = FIRST_NODE_X + (i + 1) * DISTANCE_BETWEEN_NODES;
  const centerX = (nodeX1 + NODE_WIDTH + nodeX2) / 2;

  editor.createShape({
    type: 'add-in-between-node-button',
    x: centerX - 50,
    y: TIMELINE_Y - 50,
    props: {
      w: 100,
      h: 100,
      index: i + 1,
    },
    isLocked: true,
  });
};

export const createAddNewNodeButton = (editor: Editor, chronologyLength: number) => {
  const lastNodeX = FIRST_NODE_X + (chronologyLength - 1) * DISTANCE_BETWEEN_NODES;
  editor.createShape({
    id: 'shape:add-new-node-button' as TLShapeId,
    type: 'add-new-node-button',
    x: lastNodeX + NODE_WIDTH + 70,
    y: TIMELINE_Y - 20,
    isLocked: true,
  });
};

// Function to reposition and reformat nodes after deletion with animation
export const reformatNodesAfterDeletion = (editor: Editor): Promise<void> => {
  return new Promise(async (resolve) => {
    const nodes = editor
      .getCurrentPageShapes()
      .filter((shape) => shape.type === 'visual-chronology-node')
      .sort((a: any, b: any) => a.x - b.x); // Sort by X position (left to right)

    if (nodes.length === 0) {
      resolve();
      return;
    }

    const animationDuration = 300;
    const animationPromises: Promise<void>[] = [];

    // Reposition nodes and update their alternating pattern with animation
    nodes.forEach(async (node: any, index: number) => {
      const { nodeX: newX, nodeY: newY, isAbove: newIsAbove } = calculateNodePosition(index, node.props.h);

      // Only animate if position actually changes
      const currentX = node.x;
      const currentY = node.y;
      const positionChanged = Math.abs(currentX - newX) > 5 || Math.abs(currentY - newY) > 5;

      if (positionChanged) {
        const nodeElement = document.getElementById(node.props.nodeId);
        if (nodeElement) {
          nodeElement.style.transition = `transform ${animationDuration}ms ease-in-out`;
          nodeElement.style.transform = `translate(${newX - currentX}px, ${newY - currentY}px)`;

          // Remove transition after animation
          setTimeout(() => {
            if (nodeElement) {
              nodeElement.style.transition = '';
              nodeElement.style.transform = '';
            }
          }, animationDuration);
        }
      }

      animationPromises.push(
        new Promise((r) =>
          setTimeout(
            () => {
              editor.updateShape({
                id: node.id,
                type: node.type,
                x: newX,
                y: newY,
                props: {
                  ...node.props,
                  isAbove: newIsAbove,
                },
              });

              r(void 0);
            },
            positionChanged ? animationDuration : 0,
          ),
        ),
      );

      // Find and update the connecting line
      const nodeId = node.props.nodeId;
      const lineId = `shape:connecting-line-${nodeId}` as TLShapeId;
      const line = editor.getShape(lineId);

      if (line) {
        const newLineX = newX + NODE_WIDTH / 2;

        animationPromises.push(
          new Promise((r) =>
            setTimeout(
              () => {
                // Temporarily unlock to update position, then lock again
                unlockShape(editor, line);

                editor.updateShape({
                  id: lineId,
                  type: 'line',
                  x: newLineX,
                  y: TIMELINE_Y,
                  isLocked: true,
                  props: {
                    ...line.props,
                    ...createConnectingLineProps(newIsAbove),
                  },
                });

                r(void 0);
              },
              positionChanged ? animationDuration : 0,
            ),
          ),
        );
      }
    });

    await Promise.all(animationPromises);
    resolve();
  });
};

// Enhanced deletion handler that includes reformatting
export const handleShapesDeletion = async (editor: Editor, deletedShapeIds: string[]) => {
  const nodes = editor.getCurrentPageShapes().filter((shape) => shape.type === 'visual-chronology-node');

  if (nodes.length === 0) {
    // Unlock all shapes first
    clearCanvasShapes(editor);
    return;
  }

  // Get the connecting lines for the nodes being deleted
  const connectingLines = deletedShapeIds.map((id) => {
    const lineId = id.replace('shape:visual-chronology-node-', 'shape:connecting-line-');
    return editor.getShape(lineId as TLShapeId);
  });

  // Delete the connecting lines of the nodes being deleted
  connectingLines.forEach((line) => {
    if (line) {
      // Update shape to be unlocked
      unlockShape(editor, line);

      editor.deleteShape(line.id);
    }
  });

  // Apply the re-ordering of the nodes and wait for it to complete
  await reformatNodesAfterDeletion(editor);

  // Update the add new node button to be the correct position
  const addNewNodeButton = editor.getShape('shape:add-new-node-button' as TLShapeId);

  // Find the number of chronology nodes
  const chronologyNodes = editor.getCurrentPageShapes().filter((shape) => shape.type === 'visual-chronology-node');
  const numberOfChronologyNodes = chronologyNodes.length;
  const lastNodeX = FIRST_NODE_X + (numberOfChronologyNodes - 1) * DISTANCE_BETWEEN_NODES;

  // Remove any add in between buttons or year markers which are to the right of the furthest right node
  const addInBetweenButtonsAndYearMarkers = editor
    .getCurrentPageShapes()
    .filter((shape) => shape.type === 'add-in-between-node-button' || shape.type === 'year-marker');

  addInBetweenButtonsAndYearMarkers.forEach((shape) => {
    if (shape.x > lastNodeX + NODE_WIDTH / 2) {
      // Unlock the shape before deleting it
      unlockShape(editor, shape);
      editor.deleteShape(shape.id);
    }
  });

  // Update the timeline line to be the correct length
  const timelineLine = editor.getShape('shape:timeline-line' as TLShapeId);
  if (timelineLine) {
    unlockShape(editor, timelineLine);
    editor.updateShape({
      id: timelineLine.id,
      type: 'line',
      props: {
        ...timelineLine.props,
        points: {
          '0': { id: '0', index: 'a0', x: 0, y: 0 },
          '1': { id: '1', index: 'a1', x: lastNodeX + NODE_WIDTH + 70, y: 0 },
        },
      },
      isLocked: true,
    });
  }

  if (addNewNodeButton) {
    unlockShape(editor, addNewNodeButton);
    editor.updateShape({
      id: addNewNodeButton.id,
      type: addNewNodeButton.type,
      x: lastNodeX + NODE_WIDTH + 90,
      y: TIMELINE_Y - 20,
      isLocked: true,
    });
  }
};

// Core timeline manipulation functions

export const addNode = (editor: Editor, nodeData: NodeData, position?: 'end' | number) => {
  const nodes = editor
    .getCurrentPageShapes()
    .filter((s) => s.type === 'visual-chronology-node')
    .sort((a, b) => a.x - b.x);

  if (position === 'end' || position === undefined) {
    // Add node at the end of the timeline
    const newIndex = nodes.length;
    const { nodeX, isAbove } = calculateNodePosition(newIndex, 0);

    createChronologyNode(editor, nodeData, nodeX, isAbove);

    // Add in-between button if not the first node
    if (nodes.length > 0) {
      createInBetweenButton(editor, newIndex - 1);
    }

    // Update timeline line length
    updateTimelineLength(editor);

    // Move add new node button
    moveAddNewNodeButton(editor, nodes.length + 1);

    editor.setSelectedShapes([`shape:visual-chronology-node-${nodeData.nodeId}` as TLShapeId]);
    editor.zoomToSelection({
      animation: { duration: 500, easing: EASINGS.easeInOutCubic },
    });
  } else {
    // Insert node at specific position
    insertNodeAtPosition(editor, nodeData, position);
  }
};

export const editNode = (editor: Editor, nodeId: string, updatedData: Partial<NodeData>) => {
  const nodeShape = editor.getShape(`shape:visual-chronology-node-${nodeId}` as TLShapeId);
  if (!nodeShape) return;

  const currentProps = nodeShape.props as any;
  const newProps = {
    ...currentProps,
    ...updatedData,
  };

  // Recalculate height if description or documentNames changed
  if (updatedData.description !== undefined || updatedData.documentNames !== undefined) {
    const newHeight = calculateNodeHeight(
      updatedData.description || currentProps.description,
      updatedData.documentNames || currentProps.documentNames,
    );
    newProps.h = newHeight;

    // Update node Y position if height changed
    const isAbove = currentProps.isAbove;
    const newY = calculateNodeYPosition(isAbove, newHeight);

    editor.updateShape({
      id: nodeShape.id,
      type: nodeShape.type,
      y: newY,
      props: newProps,
    });
  } else {
    editor.updateShape({
      id: nodeShape.id,
      type: nodeShape.type,
      props: newProps,
    });
  }
};

export const deleteNode = (editor: Editor, nodeId: string) => {
  const nodeShapeId = `shape:visual-chronology-node-${nodeId}` as TLShapeId;
  const nodeShape = editor.getShape(nodeShapeId);
  if (!nodeShape) return;

  // Delete the node - this will trigger the existing handleShapesDeletion
  editor.deleteShape(nodeShapeId);
};

// Helper functions for node manipulation
const insertNodeAtPosition = (editor: Editor, nodeData: NodeData, insertIndex: number) => {
  const nodes = editor
    .getCurrentPageShapes()
    .filter((s) => s.type === 'visual-chronology-node')
    .sort((a, b) => a.x - b.x);

  const { nodeX, isAbove } = calculateNodePosition(insertIndex, 0); // Height will be calculated later

  // Shift all nodes after the insertion point
  const shiftAmount = DISTANCE_BETWEEN_NODES;
  const allShapes = editor.getCurrentPageShapes();

  // Update nodes that need to be shifted
  const nodesToShift = nodes.slice(insertIndex).map((node, index) => {
    const newIndex = insertIndex + index + 1;
    const { nodeX: newX, nodeY: newY, isAbove: newIsAbove } = calculateNodePosition(newIndex, (node.props as any).h);

    return {
      id: node.id,
      type: node.type,
      x: newX,
      y: newY,
      props: {
        ...node.props,
        isAbove: newIsAbove,
      },
    };
  });

  // Update connecting lines - get the node ID from line ID to determine if above/below
  const connectingLinesToMove = allShapes
    .filter((s) => s.type === 'line' && s.id.startsWith('shape:connecting-line-'))
    .filter((s) => s.x >= nodeX)
    .map((line) => {
      // Extract nodeId from the line ID (shape:connecting-line-{nodeId})
      const nodeId = line.id.replace('shape:connecting-line-', '');
      const correspondingNode = editor.getShape(`shape:visual-chronology-node-${nodeId}` as TLShapeId);

      // Determine if the line should go up or down based on the node's isAbove property and do the opposite
      const isCurrentlyAbove = correspondingNode && (correspondingNode.props as any).isAbove;

      unlockShape(editor, line);
      return {
        id: line.id,
        type: line.type,
        x: line.x + shiftAmount,
        isLocked: true,
        props: {
          ...line.props,
          ...createConnectingLineProps(!isCurrentlyAbove),
        },
      };
    });

  // Update other elements (year markers, buttons)
  const inBetweenButtons = allShapes.filter(
    (s) => s.x >= nodeX - DISTANCE_BETWEEN_NODES / 2 && ['add-in-between-node-button'].includes(s.type),
  );
  const otherElements = allShapes.filter(
    (s) => s.x >= nodeX && ['year-marker', 'add-new-node-button'].includes(s.type),
  );

  // Create new in between button
  editor.createShape({
    type: 'add-in-between-node-button',
    x: nodeX - 75,
    y: TIMELINE_Y - 50,
    props: {
      w: 100,
      h: 100,
      index: insertIndex,
    },
    isLocked: true,
  });

  // Apply all updates
  editor.updateShapes(nodesToShift);
  editor.updateShapes(connectingLinesToMove);

  inBetweenButtons.map((shape) => {
    unlockShape(editor, shape);
  });
  editor.updateShapes(
    inBetweenButtons.map((shape) => {
      return {
        id: shape.id,
        type: shape.type,
        x: shape.x + shiftAmount,
        props: {
          ...shape.props,
          index: (shape.props as any).index + 1,
        },
        isLocked: true,
      };
    }),
  );

  otherElements.map((shape) => {
    unlockShape(editor, shape);
  });
  editor.updateShapes(
    otherElements.map((shape) => {
      return {
        id: shape.id,
        type: shape.type,
        x: shape.x + shiftAmount,
        props: shape.props,
        isLocked: true,
      };
    }),
  );

  // Create the new node
  createChronologyNode(editor, nodeData, nodeX, isAbove);

  // Update timeline length
  updateTimelineLength(editor);

  editor.setSelectedShapes([`shape:visual-chronology-node-${nodeData.nodeId}` as TLShapeId]);
  editor.zoomToSelection({
    animation: { duration: 500, easing: EASINGS.easeInOutCubic },
  });
};

const updateTimelineLength = (editor: Editor) => {
  const nodes = editor.getCurrentPageShapes().filter((s) => s.type === 'visual-chronology-node');
  const timelineLine = editor.getShape('shape:timeline-line' as TLShapeId);

  if (timelineLine) {
    const rightmostX = calculateTimelineLength(nodes);

    unlockShape(editor, timelineLine);
    editor.updateShape({
      id: timelineLine.id,
      type: 'line',
      isLocked: true,
      props: {
        ...timelineLine.props,
        points: {
          '0': { id: '0', index: 'a0', x: 0, y: 0 },
          '1': { id: '1', index: 'a1', x: rightmostX, y: 0 },
        },
      },
    });
  }
};

const moveAddNewNodeButton = (editor: Editor, chronologyLength: number) => {
  const addNewNodeButton = editor.getShape('shape:add-new-node-button' as TLShapeId);

  if (addNewNodeButton) {
    const lastNodeX = FIRST_NODE_X + (chronologyLength - 1) * DISTANCE_BETWEEN_NODES;

    unlockShape(editor, addNewNodeButton);
    editor.updateShape({
      id: addNewNodeButton.id,
      type: addNewNodeButton.type,
      x: lastNodeX + NODE_WIDTH + 100,
      y: TIMELINE_Y - 20,
      isLocked: true,
    });
  }
};

// Function to clear non-core shapes (everything except core timeline elements)
export const clearNonCoreShapes = (editor: Editor) => {
  const allShapes = editor.getCurrentPageShapes();
  const coreShapeTypes = [
    'visual-chronology-node',
    'add-new-node-button',
    'add-in-between-node-button',
    'line',
    'year-marker',
  ];

  const nonCoreShapes = allShapes.filter((shape) => !coreShapeTypes.includes(shape.type));

  nonCoreShapes.forEach((shape) => {
    unlockShape(editor, shape);
    editor.deleteShape(shape.id);
  });
};
